import React, { forwardRef, useEffect, useRef, useState } from "react";
import { Icons } from "@/assets";
import { cx } from "@/utils/cx";
import { containsOnlyDigits } from "@/utils/validation";

interface NumberInputFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "onBlur" | "value"> {
    name: string;
    label: string;
    value?: string;
    onChange?: (val: string) => void;
    onBlur?: (val: string) => void;
    error?: string;
    required?: boolean;
    uomValue?: string;
    uomOptions?: { label: string; value: string }[];
    onUomChange?: (val: string) => void;
    uomPlaceholder?: string;
    allowSpaces?: boolean;
}

export const NumberInputField = forwardRef<HTMLInputElement, NumberInputFieldProps>(({
    name,
    label,
    value,
    onChange,
    onBlur,
    error,
    required,
    disabled,
    className,
    uomValue,
    uomOptions = [],
    onUomChange,
    uomPlaceholder = "UOM",
    allowSpaces = false,
    placeholder,
    ...props
}, ref) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [isUomOpen, setIsUomOpen] = useState(false);
    const [localError, setLocalError] = useState("");

    const hasUom = uomOptions.length > 0;
    const selectedLabel = uomOptions.find((option) => String(option.value) === String(uomValue))?.label || uomPlaceholder;
    const resolvedError = error || localError;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsUomOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleValueChange = (nextValue: string) => {
        if (!nextValue) {
            setLocalError("");
            onChange?.("");
            return;
        }

        const isValidNumberValue = allowSpaces
            ? /^\d+(?: \d+)*$/.test(nextValue)
            : containsOnlyDigits(nextValue);

        if (!isValidNumberValue) {
            setLocalError("Enter number only");
            return;
        }

        setLocalError("");
        onChange?.(nextValue);
    };

    return (
        <div ref={wrapperRef} className="flex flex-col gap-2.5 w-full relative">
            <label htmlFor={name} className="text-base text-gray-90">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>

            <div
                className={cx(
                    "relative w-full rounded-[10px] p-[1.5px] transition-all duration-200 ease-in-out",
                    resolvedError ? "bg-[#ff424f]" : "bg-gray-30 focus-within:bg-gradient-to-r focus-within:from-[#00B4EA] focus-within:to-[#8A00FF]",
                    disabled && "opacity-60 cursor-not-allowed bg-gray-200"
                )}
            >
              <div className="relative flex h-[51px] items-center rounded-[8.5px] bg-white">
                <input
                    ref={ref}
                    id={name}
                    name={name}
                    type="text"
                    inputMode="numeric"
                    value={value || ""}
                    disabled={disabled}
                    placeholder={placeholder}
                    onChange={(event) => handleValueChange(event.target.value)}
                    onBlur={(event) => onBlur?.(event.target.value)}
                    className={cx(
                        "h-full min-w-0 flex-1 px-5 text-sm text-black placeholder:text-gray-60 outline-none bg-transparent",
                        disabled && "cursor-not-allowed",
                        className
                    )}
                    {...props}
                />

                {resolvedError && (
                    <div className={cx("pointer-events-none flex items-center", hasUom ? "mr-3" : "mr-4")}>
                        <Icons.ErrorIcon className="w-5 h-5 text-[#ff424f]" />
                    </div>
                )}

                {hasUom && (
                    <>
                        <button
                            type="button"
                            disabled={disabled}
                            onClick={() => setIsUomOpen((open) => !open)}
                            className={cx(
                                "flex h-[38px] items-center justify-center gap-2 border-l border-gray-30 pl-2",
                                "text-base text-gray-70 outline-none transition-colors",
                                disabled ? "cursor-not-allowed" : "hover:text-gray-90"
                            )}
                        >
                            <span className="truncate">{selectedLabel}</span>
                            <Icons.ChevronDownIcon className={cx("w-3.5 h-3.5 shrink-0 text-gray-500 transition-transform", isUomOpen && "rotate-180")} />
                        </button>

                        {isUomOpen && !disabled && (
                            <div className="absolute top-[calc(100%+6px)] right-0 min-w-[118px] max-w-[200px] bg-white border border-gray-200 rounded-md shadow-lg max-h-[200px] overflow-y-auto z-50">
                                {uomOptions.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={() => {
                                            onUomChange?.(option.value);
                                            setIsUomOpen(false);
                                        }}
                                        className={cx(
                                            "w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50",
                                            String(option.value) === String(uomValue) ? "text-[#0a68ff] font-medium" : "text-gray-700"
                                        )}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                )}
              </div>
            </div>

            {resolvedError && (
                <p className="text-xs text-error-60 font-medium">{resolvedError}</p>
            )}
        </div>
    );
});

NumberInputField.displayName = "NumberInputField";
